/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useMemo, useRef, useEffect } from "react";
import { type FilterColumn, type FilterConditionEntry } from "@CubeComponents/html/SlickGrid1/Modals/FilterButton";
import {
  getConditionMet,
  applyLogicToResult,
  collectAncestorIds,
  type FilterType,
  type QueryOperator,
} from "@CubeComponents/html/SlickGrid1/filterUtils";
import { ColumnData } from "@WebTypes/scheduleDomain";
// The Gantt library's own column type — `gantt.config.columns` is typed with this one, not the
// look-alike in `@WebTypes/scheduleDomain`.
import type { GanttStatic, GridColumn, Task } from "./dhtmlxgantt";

declare const gantt: GanttStatic;

const GANTT_COLUMN_FILTER_TYPE_MAP: Record<string, string> = {
  TaskDisp: "Text",
  text: "Text",
  start_date: "Date",
  end_date: "Date",
  duration: "Number",
  actualStartDate: "Date",
  actualEndDate: "Date",
  plannedStartDate: "Date",
  plannedEndDate: "Date",
  plannedDuration: "Number",
  remDuration: "Number",
  progress: "Number",
  totalSlack: "Number",
  critical: "Text",
  "planned%": "Number",
  startVariance: "Number",
  finishVariance: "Number",
  predecessor: "Text",
  successor: "Text",
};

/**
 * `FilterConditionEntry` types a saved condition's `type`/`option` as plain `string` — the blob is
 * free-form server JSON — so both are CHECKED against the unions the filter helpers switch on
 * instead of being asserted into them.
 */
const FILTER_TYPES = ["Text", "Date", "Number", "MultiSelect", "FileSize"] as const;
const QUERY_OPERATORS = [
  "startswith",
  "endswith",
  "empty",
  "equal",
  "contains",
  "like",
  "doesnotstartwith",
  "doesnotendwith",
  "doesnotcontain",
  "notequal",
  "notempty",
  "notnull",
  "null",
  "greaterthan",
  "greaterthanorequal",
  "lessthan",
  "lessthanorequal",
  "between",
] as const;

const isFilterType = (value: unknown): value is FilterType => FILTER_TYPES.some((type) => type === value);
const isQueryOperator = (value: unknown): value is QueryOperator =>
  QUERY_OPERATORS.some((operator) => operator === value);

const computeNumericFromTemplate = (task: Task, field: string): number | null => {
  const column = gantt.config.columns.find((c: GridColumn) => c.name === field);
  if (!column?.template) return task[field] ?? null;
  const rendered = column.template(task);
  if (rendered === undefined || rendered === null || rendered === "") return null;
  const numericValue = parseFloat(String(rendered).replace("%", "").trim());
  return Number.isNaN(numericValue) ? null : numericValue;
};

export const getGanttTaskFieldValue = (task: Task, field: string): unknown => {
  switch (field) {
    case "start_date":
    case "end_date":
    case "actualStartDate":
    case "actualEndDate":
    case "plannedStartDate":
    case "plannedEndDate":
      return task[field] ? new Date(task[field]).getTime() : null;
    case "progress":
      return task.progress != null ? Math.round(task.progress * 100) : null;
    case "critical":
      if (task.critical) return task.critical === "1" ? "Yes" : "No";
      return gantt.isCriticalTask(task) ? "Yes" : "No";
    case "remDuration":
    case "totalSlack":
    case "planned%":
    case "startVariance":
    case "finishVariance":
    case "plannedDuration":
    case "duration":
      return computeNumericFromTemplate(task, field);
    case "predecessor": {
      const preds = (task.$target || []).map((linkId: string | number) => {
        const link = gantt.getLink(linkId);
        // `getTask` is DECLARED to return a `Task`, but for an id the store does not hold it only
        // *reports* the miss through `gantt.assert` (which logs, it does not throw) and returns
        // `undefined`. Asking the store first keeps that case handled without contradicting the
        // vendored declaration.
        return gantt.isTaskExists(link.source) ? gantt.getTask(link.source).TaskDisp : undefined;
      });
      return preds.join(", ");
    }
    case "successor": {
      const succs = (task.$source || []).map((linkId: string | number) => {
        const link = gantt.getLink(linkId);
        // Same as the predecessor branch above: a link endpoint that is no longer in the store
        // lands `undefined` here despite the declared return type.
        return gantt.isTaskExists(link.target) ? gantt.getTask(link.target).TaskDisp : undefined;
      });
      return succs.join(", ");
    }
    default:
      return task[field];
  }
};

interface UseGanttFiltersArgs {
  ganttDataAvailable: boolean;
  savedColumns: ColumnData[];
  rerenderGantt: boolean | null;
}

interface UseGanttFiltersReturn {
  ganttFilterColumns: FilterColumn[];
  ganttTasksDataset: Task[];
  handleGanttFilterChange: (filterConditionsObj: Record<string, FilterConditionEntry>) => void;
  getGanttTaskFieldValue: typeof getGanttTaskFieldValue;
}

export const useGanttFilters = ({
  ganttDataAvailable,
  savedColumns,
  rerenderGantt,
}: UseGanttFiltersArgs): UseGanttFiltersReturn => {
  const ganttFilterEventId = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (ganttFilterEventId.current) {
        gantt.detachEvent(ganttFilterEventId.current);
        ganttFilterEventId.current = null;
      }
    };
  }, []);

  const ganttFilterColumns: FilterColumn[] = useMemo(() => {
    return gantt.config.columns
      .filter((col: GridColumn) => col.name && col.name !== "add" && col.name !== "CustomIcon" && !col.hide)
      .map((col: GridColumn) => {
        // The vendored typings declare `name` as `string | number` and `label` as `any`; the filter
        // UI keys everything by a string, so both are stringified rather than asserted into one.
        const field = String(col.name);
        return {
          field,
          name: String(col.label || field),
          filterType: GANTT_COLUMN_FILTER_TYPE_MAP[field] || "Text",
          id: field,
        };
      });
  }, [ganttDataAvailable, savedColumns, rerenderGantt]);

  const ganttTasksDataset = useMemo(() => {
    try {
      // Guarded by the catch, not by an optional call: if the gantt bundle is not up yet, reading or
      // calling `getTaskByTime` throws and the empty dataset below is returned.
      return gantt.getTaskByTime();
    } catch {
      return [];
    }
  }, [ganttDataAvailable]);

  const handleGanttFilterChange = (filterConditionsObj: Record<string, FilterConditionEntry>) => {
    if (ganttFilterEventId.current) {
      gantt.detachEvent(ganttFilterEventId.current);
      ganttFilterEventId.current = null;
    }

    const conditionKeys = Object.keys(filterConditionsObj);

    if (conditionKeys.length > 0) {
      const matchedIds: (string | number)[] = [];
      gantt.eachTask((task: Task) => {
        let result = false;
        for (let i = 0; i < conditionKeys.length; i++) {
          // Both come straight from `Object.keys` of the same object, so the lookup and the `id` the
          // filter modal always writes are present. Erased assertions.
          const condition = filterConditionsObj[conditionKeys[i]!]!;
          const fieldValue = getGanttTaskFieldValue(task, condition.id!);
          // An unrecognised type or operator already meant "no match": every applier looks its
          // operator up in a map and falls back to `false`, and so does `getConditionMet` for the
          // type. MultiSelect is the one type whose applier ignores the operator, so it is not gated
          // on having a valid one.
          const filterType = condition.type;
          const filterOption = condition.option;
          let conditionMet = false;
          if (isFilterType(filterType) && (isQueryOperator(filterOption) || filterType === "MultiSelect")) {
            conditionMet = getConditionMet(
              filterType,
              fieldValue,
              isQueryOperator(filterOption) ? filterOption : "equal",
              condition.text,
            );
          }

          if (i === 0) {
            result = conditionMet;
          } else {
            result = applyLogicToResult(result, conditionMet, condition.logic?.toLowerCase() || "and");
          }
        }
        if (result) matchedIds.push(task.id);
      });

      const matchSet = collectAncestorIds(matchedIds, (id) => {
        if (!gantt.isTaskExists(id)) return null;
        const parentId = gantt.getTask(id).parent;
        return parentId && parentId !== 0 ? parentId : null;
      });

      ganttFilterEventId.current = gantt.attachEvent(
        "onBeforeTaskDisplay",
        function (_id: string | number, task: Task) {
          return matchSet.has(task.id);
        },
      );
    }

    gantt.render();
  };

  return { ganttFilterColumns, ganttTasksDataset, handleGanttFilterChange, getGanttTaskFieldValue };
};
